import BASE from "./config";

const stripHTMLTags = (htmlString) => {
    return htmlString ? htmlString.replace(/<\/?[^>]+(>|$)/g, "") : "";
};

const normalizePath = (path) => String(path || "").replace(/\\/g, "/").trim();

const toGalleryUrl = (path) => {
    const cleaned = normalizePath(path).replace(/^property\//, "");
    if (!cleaned) return "";
    return `${BASE.BASE_IMAGES_PATH}/property/${cleaned}`;
};

/**
 * Build all candidate image URLs for a property box:
 * main image first, then gallery images in order (1st, 2nd, 3rd...).
 */
export const getPropertyImageUrls = (property) => {
    if (!property) return [];

    const urls = [];
    const push = (url) => {
        if (url && !urls.includes(url)) {
            urls.push(url);
        }
    };

    const mainImage = normalizePath(property.image);
    if (mainImage) {
        // Backend fallback may already prefix with property/
        if (mainImage.startsWith("property/")) {
            push(`${BASE.BASE_IMAGES_PATH}/${mainImage}`);
        } else {
            push(`${BASE.BASE_IMAGES_PATH}/${mainImage}`);
            // Also try gallery folder in case main path was stored without prefix
            push(toGalleryUrl(mainImage));
        }
    }

    const gallery = [...(property.property_images || [])].sort(
        (a, b) => Number(a.order ?? a.sort_order ?? 0) - Number(b.order ?? b.sort_order ?? 0)
    );

    for (const record of gallery) {
        const paths = Array.isArray(record?.image) ? record.image : [record?.image];
        for (const path of paths) {
            push(toGalleryUrl(path));
        }
    }

    return urls;
};

/** First candidate URL (kept for simple call sites). */
export const getPropertyImageUrl = (property) => getPropertyImageUrls(property)[0] || "";

const listIds = (list) =>
    (Array.isArray(list) ? list : [])
        .map((item) => Number(item?.id))
        .filter((id) => !Number.isNaN(id));

const listNames = (list) =>
    (Array.isArray(list) ? list : []).map((item) => String(item?.name || "").toLowerCase());

export const isWithin1kmToBeach = (value) => {
    if (value == null || value === "") return false;
    const normalized = String(value).trim().toLowerCase();
    if (normalized === "1" || normalized.includes("less than 1")) return true;
    if (normalized === "2" || normalized.includes("more than 1")) return false;
    const km = parseFloat(normalized);
    return !Number.isNaN(km) && km > 0 && km <= 1;
};

const hasPoolAmenity = (property) => {
    const amenityIds = listIds(property?.amenity);
    const amenityNames = listNames(property?.amenity);
    const poolIds = new Set([1, 3, 14, 22, 25, 30]); // private, shallow end, cover, heated, indoor, fenced
    if (amenityIds.some((id) => poolIds.has(id))) return true;
    return amenityNames.some((name) => {
        if (!name.includes("pool")) return false;
        if (name.includes("towel")) return false;
        if (name.includes("table")) return false;
        return true;
    });
};

/**
 * Feature IDs are not unique across amenity / general / entertainment tables.
 * Match each search filter against the correct field (and names as fallback).
 */
export const propertyHasFeature = (property, featureId) => {
    if (!property) return false;
    if (featureId === "beach") {
        return isWithin1kmToBeach(property.distance_to_beach);
    }

    const id = Number(featureId);
    const amenityIds = listIds(property.amenity);
    const generalIds = listIds(property.generals);
    const entertainmentIds = listIds(property.entertainments);
    const amenityNames = listNames(property.amenity);
    const generalNames = listNames(property.generals);
    const entertainmentNames = listNames(property.entertainments);
    const heatingPrice = parseFloat(String(property.pool_heating_price ?? "").replace(/[^\d.-]/g, ""));
    const hasHeatingExtra = !Number.isNaN(heatingPrice) && heatingPrice > 0;

    switch (id) {
        case 8: // Internet Access (amenity 8 is Balcony)
            return generalIds.includes(8) || generalNames.some((name) => name.includes("internet"));
        case 22: // Heated Pool
            return amenityIds.includes(22)
                || amenityNames.some((name) => name.includes("heated") && name.includes("pool"))
                || (hasHeatingExtra && hasPoolAmenity(property));
        case 1: // Private Pool (general 1 is Real Fire, entertainment 1 is Terrestrial TV)
            return amenityIds.includes(1) || amenityNames.some((name) => name.includes("private") && name.includes("pool"));
        case 32: // Child Poolgate
            return amenityIds.includes(32) || amenityNames.some((name) => name.includes("poolgate") || name.includes("pool gate"));
        case 20: // Jacuzzi
            return amenityIds.includes(20) || amenityNames.some((name) => name.includes("jacuzzi"));
        case 5: // Tennis Court (amenity 29 and/or entertainment 5)
            return amenityIds.includes(29)
                || amenityIds.includes(5)
                || entertainmentIds.includes(5)
                || amenityNames.some((name) => name.includes("tennis"))
                || entertainmentNames.some((name) => name.includes("tennis"));
        case 12: // Games Room
            return entertainmentIds.includes(12) || entertainmentNames.some((name) => name.includes("games room"));
        default:
            return amenityIds.includes(id) || generalIds.includes(id) || entertainmentIds.includes(id);
    }
};

const CURRENCY_SYMBOLS = {
    EUR: "€",
    EURO: "€",
    GBP: "£",
    USD: "$",
};

export const getCurrencySymbol = (currency) => {
    if (currency == null || currency === "") return "€";
    if (typeof currency === "object") {
        if (currency.symbol) return currency.symbol;
        return getCurrencySymbol(currency.code);
    }
    const raw = String(currency).trim();
    const code = raw.toUpperCase();
    if (CURRENCY_SYMBOLS[code]) return CURRENCY_SYMBOLS[code];
    if (/^[€£$¥]/.test(raw)) return raw.charAt(0);
    return raw;
};

export const getPropertyCurrencySymbol = (propertyOrPrices, preferredPrice) => {
    if (preferredPrice?.currency) {
        return getCurrencySymbol(preferredPrice.currency);
    }
    const prices = Array.isArray(propertyOrPrices)
        ? propertyOrPrices
        : propertyOrPrices?.property_prices;
    const withCurrency = (prices || []).find((entry) => entry?.currency);
    return getCurrencySymbol(withCurrency?.currency);
};

export default stripHTMLTags;
